import { useEffect, useMemo, useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Map as MapIcon, Layers, Building2, Filter, X, ChevronDown, ChevronUp, Navigation } from 'lucide-react';
import { toast } from 'sonner';
import { MapContainer, TileLayer, Marker, Popup, useMap, CircleMarker } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { mapApi, crmApi, type ApiRecord } from '../../lib/api';
import { SurfaceCard } from '../../components/ui/SurfaceCard';

// Industry colors matching backend
type IndustryOption = { value: string; label: string; color: string };

const DEFAULT_INDUSTRY_OPTIONS: IndustryOption[] = [
	{ value: 'veterinarian', label: 'Veterinarian', color: '#10b981' },
	{ value: 'dentist', label: 'Dentist', color: '#06b6d4' },
	{ value: 'medical', label: 'Medical', color: '#0ea5e9' },
	{ value: 'manufacturing', label: 'Manufacturing', color: '#f59e0b' },
	{ value: 'insurance', label: 'Insurance', color: '#8b5cf6' },
	{ value: 'technology', label: 'Technology', color: '#3b82f6' },
	{ value: 'retail', label: 'Retail', color: '#ec4899' },
	{ value: 'finance', label: 'Finance', color: '#14b8a6' },
	{ value: 'education', label: 'Education', color: '#f97316' },
	{ value: 'real_estate', label: 'Real Estate', color: '#84cc16' },
	{ value: 'construction', label: 'Construction', color: '#ef4444' },
	{ value: 'hospitality', label: 'Hospitality', color: '#a855f7' },
	{ value: 'legal', label: 'Legal', color: '#64748b' },
	{ value: 'other', label: 'Other', color: '#6b7280' },
];

const LEAD_STATUS_OPTIONS = [
	{ value: 'new', label: 'New', color: 'bg-slate-500' },
	{ value: 'attempted', label: 'Attempted', color: 'bg-yellow-500' },
	{ value: 'contacted', label: 'Contacted', color: 'bg-blue-500' },
	{ value: 'interested', label: 'Interested', color: 'bg-accent' },
	{ value: 'follow_up', label: 'Follow Up', color: 'bg-orange-500' },
	{ value: 'future', label: 'Future', color: 'bg-purple-500' },
	{ value: 'not_interested', label: 'Not Interested', color: 'bg-red-500' },
	{ value: 'converted', label: 'Converted', color: 'bg-green-500' },
];

// Fix for default Leaflet marker icons
const createCustomIcon = (color: string) => {
	return L.divIcon({
		className: 'custom-marker',
		html: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="${color}" width="28" height="28" style="filter: drop-shadow(0 2px 2px rgba(0,0,0,0.3));">
			<path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
			<circle cx="12" cy="9" r="2.5" fill="white"/>
		</svg>`,
		iconSize: [28, 28],
		iconAnchor: [14, 28],
		popupAnchor: [0, -28]
	});
};

// Map bounds handler component
function MapBoundsHandler({ onBoundsChange }: { onBoundsChange: (bounds: L.LatLngBounds) => void }) {
	const map = useMap();

	useEffect(() => {
		const handleMoveEnd = () => {
			onBoundsChange(map.getBounds());
		};

		map.on('moveend', handleMoveEnd);
		onBoundsChange(map.getBounds()); // Initial bounds

		return () => {
			map.off('moveend', handleMoveEnd);
		};
	}, [map, onBoundsChange]);

	return null;
}

// Fly to location handler
function FlyToHandler({ target }: { target: [number, number] | null }) {
	const map = useMap();

	useEffect(() => {
		if (target) {
			map.flyTo(target, 15, { duration: 1.5 });
		}
	}, [map, target]);

	return null;
}

export function MapPage() {
	const [selectedIndustries, setSelectedIndustries] = useState<string[]>([]);
	const [selectedLeadStatuses, setSelectedLeadStatuses] = useState<string[]>([]);
	const [searchQuery, setSearchQuery] = useState('');
	const [showFilters, setShowFilters] = useState(true);
	const [mapBounds, setMapBounds] = useState<L.LatLngBounds | null>(null);
	const [flyToTarget, setFlyToTarget] = useState<[number, number] | null>(null);
	const [selectedCompany, setSelectedCompany] = useState<ApiRecord | null>(null);

	// Fetch industry types
	const { data: industryTypes = DEFAULT_INDUSTRY_OPTIONS } = useQuery({
		queryKey: ['map-industry-types'],
		queryFn: mapApi.industryTypes,
		initialData: DEFAULT_INDUSTRY_OPTIONS
	});

	// Fetch companies with filters
	const { data: companies = [], isLoading } = useQuery({
		queryKey: ['map-companies', selectedIndustries, selectedLeadStatuses, searchQuery, mapBounds],
		queryFn: () => {
			const params: Record<string, string> = {};
			if (selectedIndustries.length > 0) {
				params.industries = selectedIndustries.join(',');
			}
			if (selectedLeadStatuses.length > 0) {
				params.lead_statuses = selectedLeadStatuses.join(',');
			}
			if (searchQuery) {
				params.search = searchQuery;
			}
			if (mapBounds) {
				params.north = String(mapBounds.getNorth());
				params.south = String(mapBounds.getSouth());
				params.east = String(mapBounds.getEast());
				params.west = String(mapBounds.getWest());
			}
			return mapApi.companies(params);
		},
		enabled: true
	});

	// Build industry color map
	const industryColorMap = useMemo(() => {
		const map: Record<string, string> = {};
		industryTypes.forEach((ind: IndustryOption) => {
			map[ind.value] = ind.color;
		});
		return map;
	}, [industryTypes]);

	// Filter toggle helpers
	const toggleIndustry = useCallback((value: string) => {
		setSelectedIndustries(prev =>
			prev.includes(value)
				? prev.filter(i => i !== value)
				: [...prev, value]
		);
	}, []);

	const toggleLeadStatus = useCallback((value: string) => {
		setSelectedLeadStatuses(prev =>
			prev.includes(value)
				? prev.filter(s => s !== value)
				: [...prev, value]
		);
	}, []);

	// Clear all filters
	const clearFilters = useCallback(() => {
		setSelectedIndustries([]);
		setSelectedLeadStatuses([]);
		setSearchQuery('');
	}, []);

	// Navigate to company
	const navigateToCompany = useCallback((company: ApiRecord) => {
		const lat = company.latitude as number;
		const lng = company.longitude as number;
		if (lat && lng) {
			setFlyToTarget([lat, lng]);
			setSelectedCompany(company);
		}
	}, []);

	// Center of US as default
	const defaultCenter: [number, number] = [39.8283, -98.5795];
	const defaultZoom = 4;

	return (
		<div className="flex h-[calc(100vh-4rem)]">
			{/* Sidebar Filters */}
			<div className={`bg-surface-2 border-r border-subtle transition-all duration-300 ${showFilters ? 'w-80' : 'w-0 overflow-hidden'}`}>
				<div className="p-4 space-y-4 h-full overflow-y-auto">
					{/* Header */}
					<div className="flex items-center justify-between">
						<h2 className="text-lg font-semibold text-primary flex items-center gap-2">
							<Filter className="w-5 h-5" />
							Filters
						</h2>
						<button
							onClick={clearFilters}
							className="text-xs text-muted hover:text-secondary transition-colors"
						>
							Clear all
						</button>
					</div>

					{/* Search */}
					<div>
						<input
							type="text"
							placeholder="Search companies..."
							value={searchQuery}
							onChange={(e) => setSearchQuery(e.target.value)}
							className="w-full px-3 py-2 bg-surface-hover border border-subtle rounded-lg text-sm text-secondary placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-blue-500/50"
						/>
					</div>

					{/* Industry Filter */}
					<div className="space-y-2">
						<h3 className="text-sm font-medium text-secondary flex items-center gap-2">
							<Building2 className="w-4 h-4" />
							Industry Types
						</h3>
						<div className="space-y-1 max-h-48 overflow-y-auto">
							{industryTypes.map((industry: IndustryOption) => (
								<label
									key={industry.value}
									className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-surface-hover cursor-pointer transition-colors"
								>
									<input
										type="checkbox"
										checked={selectedIndustries.includes(industry.value)}
										onChange={() => toggleIndustry(industry.value)}
										className="w-4 h-4 rounded border-strong bg-surface-hover text-blue-500 focus:ring-blue-500/50"
									/>
									<span
										className="w-3 h-3 rounded-full"
										style={{ backgroundColor: industry.color }}
									/>
									<span className="text-sm text-secondary">{industry.label}</span>
								</label>
							))}
						</div>
					</div>

					{/* Lead Status Filter */}
					<div className="space-y-2">
						<h3 className="text-sm font-medium text-secondary">Lead Status</h3>
						<div className="flex flex-wrap gap-1">
							{LEAD_STATUS_OPTIONS.map((status) => (
								<button
									key={status.value}
									onClick={() => toggleLeadStatus(status.value)}
									className={`px-2 py-1 text-xs rounded-md transition-colors ${
										selectedLeadStatuses.includes(status.value)
											? `${status.color} text-primary`
											: 'bg-surface-hover text-muted hover:bg-surface-hover'
									}`}
								>
									{status.label}
								</button>
							))}
						</div>
					</div>

					{/* Results count */}
					<div className="pt-4 border-t border-subtle">
						<p className="text-sm text-muted">
							<span className="font-medium text-secondary">{companies.length}</span> companies on map
						</p>
					</div>
				</div>
			</div>

			{/* Map Area */}
			<div className="flex-1 relative">
				{/* Toggle filters button */}
				<button
					onClick={() => setShowFilters(!showFilters)}
					className="absolute top-4 left-4 z-[1000] p-2 bg-surface-2/90 backdrop-blur border border-subtle rounded-lg shadow-lg hover:bg-surface-hover transition-colors"
				>
					{showFilters ? <ChevronLeft className="w-5 h-5 text-secondary" /> : <Filter className="w-5 h-5 text-secondary" />}
				</button>

				{/* Loading indicator */}
				{isLoading && (
					<div className="absolute top-4 right-4 z-[1000] px-3 py-1.5 bg-surface-2/90 backdrop-blur border border-subtle rounded-lg">
						<span className="text-sm text-secondary">Loading...</span>
					</div>
				)}

				<MapContainer
					center={defaultCenter}
					zoom={defaultZoom}
					style={{ height: '100%', width: '100%' }}
					zoomControl={false}
				>
					<TileLayer
						attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
						url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
					/>
					<MapBoundsHandler onBoundsChange={setMapBounds} />
					<FlyToHandler target={flyToTarget} />

					{companies.map((company) => {
						const lat = company.latitude as number;
						const lng = company.longitude as number;
						if (!lat || !lng) return null;

						const industry = (company.industry_type as string) || 'other';
						const color = industryColorMap[industry] || '#6b7280';
						const isSelected = selectedCompany?.id === company.id;

						return (
							<Marker
								key={company.id as number}
								position={[lat, lng]}
								icon={createCustomIcon(color)}
								eventHandlers={{
									click: () => setSelectedCompany(company)
								}}
							>
								<Popup>
									<CompanyPopup
										company={company}
										onNavigate={() => navigateToCompany(company)}
									/>
								</Popup>
							</Marker>
						);
					})}
				</MapContainer>

				{/* Selected company details panel */}
				{selectedCompany && (
					<CompanyDetailPanel
						company={selectedCompany}
						onClose={() => setSelectedCompany(null)}
						industryColor={industryColorMap[(selectedCompany.industry_type as string) || 'other'] || '#6b7280'}
					/>
				)}

				{/* Legend */}
				<div className="absolute bottom-4 left-4 z-[1000] p-3 bg-surface-2/90 backdrop-blur border border-subtle rounded-lg shadow-lg">
					<h4 className="text-xs font-medium text-muted mb-2">Industries</h4>
					<div className="grid grid-cols-2 gap-x-4 gap-y-1">
						{industryTypes.slice(0, 6).map((industry: IndustryOption) => (
							<div key={industry.value} className="flex items-center gap-1.5">
								<span
									className="w-2 h-2 rounded-full"
									style={{ backgroundColor: industry.color }}
								/>
								<span className="text-xs text-secondary">{industry.label}</span>
							</div>
						))}
					</div>
				</div>
			</div>
		</div>
	);
}

// Company Popup component
function CompanyPopup({ company, onNavigate }: { company: ApiRecord; onNavigate: () => void }) {
	const industry = String(company.industry_type || 'other');
	const label = DEFAULT_INDUSTRY_OPTIONS.find(i => i.value === industry)?.label || industry;
	const name = String(company.name || 'Unknown');
	const address = company.address ? String(company.address) : null;
	const city = company.city ? String(company.city) : null;
	const state = company.state ? String(company.state) : null;

	return (
		<div className="p-2 min-w-[200px]">
			<h3 className="font-semibold text-slate-800">{name}</h3>
			<p className="text-sm text-slate-600 capitalize">{label}</p>
			{address && (
				<p className="text-xs text-muted mt-1">{address}</p>
			)}
			{(city || state) && (
				<p className="text-xs text-muted">
					{[city, state].filter(Boolean).join(', ')}
				</p>
			)}
			<div className="mt-3 flex gap-2">
				<button
					onClick={onNavigate}
					className="flex-1 px-3 py-1.5 bg-blue-500 hover:bg-blue-600 text-primary text-xs font-medium rounded-md transition-colors"
				>
					View Details
				</button>
				<a
					href={`/companies?id=${company.id}`}
					className="px-3 py-1.5 bg-surface-hover hover:bg-surface-hover text-secondary text-xs font-medium rounded-md transition-colors"
				>
					Open
				</a>
			</div>
		</div>
	);
}

// Company Detail Panel component
function CompanyDetailPanel({ company, onClose, industryColor }: { company: ApiRecord; onClose: () => void; industryColor: string }) {
	const industry = String(company.industry_type || 'other');
	const label = DEFAULT_INDUSTRY_OPTIONS.find(i => i.value === industry)?.label || industry;
	const name = String(company.name || 'Unknown');
	const websiteDomain = company.website_domain ? String(company.website_domain) : null;
	const address = company.address ? String(company.address) : null;
	const city = company.city ? String(company.city) : null;
	const state = company.state ? String(company.state) : null;
	const postalCode = company.postal_code ? String(company.postal_code) : null;
	const country = company.country ? String(company.country) : null;
	const contactCount = Number(company.contact_count || 0);
	const leadStatus = String(company.lead_status || 'new');
	const companyId = company.id;

	return (
		<div className="absolute top-4 right-4 z-[1000] w-80 bg-surface-2/95 backdrop-blur border border-subtle rounded-xl shadow-xl">
			<div className="p-4">
				<div className="flex items-start justify-between">
					<div>
						<div className="flex items-center gap-2">
							<span
								className="w-3 h-3 rounded-full"
								style={{ backgroundColor: industryColor }}
							/>
							<h3 className="font-semibold text-primary">{name}</h3>
						</div>
						<p className="text-sm text-muted mt-1 capitalize">{label}</p>
					</div>
					<button
						onClick={onClose}
						className="p-1 hover:bg-surface-hover rounded-lg transition-colors"
					>
						<X className="w-5 h-5 text-muted" />
					</button>
				</div>

				<div className="mt-4 space-y-3">
					{websiteDomain && (
						<div className="text-sm">
							<span className="text-muted">Website:</span>{' '}
							<a
								href={`https://${websiteDomain}`}
								target="_blank"
								rel="noopener noreferrer"
								className="text-blue-400 hover:text-blue-300"
							>
								{websiteDomain}
							</a>
						</div>
					)}

					{(address || city || state) && (
						<div className="text-sm text-secondary">
							<span className="text-muted">Address:</span>
							<div className="mt-1">
								{address && <p>{address}</p>}
								<p>
									{[city, state, postalCode]
										.filter(Boolean)
										.join(', ')}
								</p>
								{country && <p>{country}</p>}
							</div>
						</div>
					)}

					<div className="text-sm">
						<span className="text-muted">Contacts:</span>{' '}
						<span className="text-secondary">{contactCount}</span>
					</div>

					<div className="text-sm">
						<span className="text-muted">Lead Status:</span>{' '}
						<span className="text-secondary capitalize">{leadStatus}</span>
					</div>
				</div>

				<div className="mt-4 pt-4 border-t border-subtle">
					<a
						href={`/companies?id=${companyId}`}
						className="block w-full py-2 px-4 bg-blue-600 hover:bg-blue-500 text-primary text-sm font-medium text-center rounded-lg transition-colors"
					>
						View Full Profile
					</a>
				</div>
			</div>
		</div>
	);
}

// ChevronLeft icon component (inline since it wasn't imported)
function ChevronLeft(props: { className?: string }) {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			width="24"
			height="24"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
			className={props.className}
		>
			<path d="m15 18-6-6 6-6" />
		</svg>
	);
}
